/**
 * Balcony sınıfı, balkon elemanlarını temsil eder.
 *
 * Özellikler:
 * - _executor: Balkon geometrisini oluşturan executor
 * - _profileData: Profil verilerini içeren nesne
 * - _meter: Ölçü birimi göstergesi
 * - _base2d: 2D görünüm için temel mesh
 * - glassColor: Cam rengi
 * - profileColor: Profil rengi
 * - fixationName: Sabitleme profili adı
 * - fixation: Sabitleme tipi
 * - _isMeterVisible: Ölçü biriminin görünürlüğü
 *
 * Metodlar:
 * - _applyParameters: Malzeme parametrelerini uygular
 */

import { Color, Euler, Scene, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils.js";
import { CameraInterface, Engine } from "../../engine/Engine";
import { IItem } from "../../core/ui-kit/IPaneAdapter";
import { logger } from "../../util/Logger";
import { next, nextWall, prev, prevWall } from "../../util/SceneUtil";
import { Meter } from "../Meter";
import { Point } from "../Point";
import { AbstractWall } from "./AbstractWall";
import { BalconyExporter } from "./balcony/BalconyExporter";
import { createExecutor } from "./balcony/executors";
import { GeometryNodeExecutor } from "./balcony/GeometryNodeExecutor";
import {
  baseMaterial,
  glassInterMaterial,
  glassMaterial,
  glassSideMaterial,
} from "./balcony/Materials";
import { ExportAccumulator, WallExportData } from "./balcony/types";
import { Register } from "../../class/ClassRegistry";

@Register("Balcony")
export class Balcony extends AbstractWall {
  private _executor?: GeometryNodeExecutor;
  private _profileData: any = {};

  private _meterVertical?: Meter;

  public glassColor = "Extra Clear";
  public profileColor = "Mat RAL 7016";
  public fixationName = "Profil SB360 Top Mount";
  protected fixation = 0;
  protected _isMeterVisible = true;

  constructor(start: Point, end: Point, sizeInfo?: any) {
    super(start, end);
    this._type = "Balcony";
    this._name = "Balcony_" + generateUUID();
    // this._rayTarget.name = "RayTarget" + this._name;
    this._isVisible = true;
    if (this._root) {
      this._root.name = "Root" + this._name;
      this._root.userData["parent"] = this;
    }

    this.raycastable = false;
    this._color2d = new Color(0x5555dd);
    this._thickness = 0.02;

    Engine.activeEngine.addEventListener("select-custom-settings", (event) => {
      const e = event as any;
      e.detail.item.value = e.detail.value;

      this.setOption(e.detail.group, e.detail.item);
    });
  }

  protected initializeMeter(): void {
    super.initializeMeter();
    this._meter.name = "Balcony";
  }

  set profileData(data: any) {
    this._profileData = data;
    this.setAttributes("profileId", data.id);
    this.isDirty = true;
  }

  get profileData() {
    return this._profileData;
  }

  private _glassData: any;

  set glassData(data: any) {
    this._glassData = data;
    this.setAttributes("glassId", data?.id);
    this.isDirty = true;
  }

  get glassData() {
    return this._glassData;
  }

  setAttributes(name: string, value: any): void {
    super.setAttributes(name, value);
    if (name == "profileId") {
      this._executor?.dispose();
      this._executor = createExecutor(value);
      if (this._executor) this._executor.line = this;
    }
    this._executor?.setAttribute(name, value);
  }

  private static _applyParameters(material: any, ref?: any) {
    Object.keys(ref).forEach((key) => {
      logger.info("key", key);
      if (material?.hasOwnProperty(key)) {
        if (key === "color") {
          material[key] = new Color(ref[key]);
        } else {
          material[key] = ref[key];
        }
      }
    });
  }

  setOption(group: any, item: any) {
    group?.items?.forEach((e: IItem) => {
      e.selected = false;
    });

    item.selected = true;
    if (!group) {
      this.setAttributes(item.key, item.value);
    } else if (group.name == "Glass Exist") {
    } else if (group.name == "Glass Height") {
      //this.glassHeight = item.ref;
      this.sizeInfo.glassHeight = item.ref;
      this.setAttributes("glassHeight", item.ref);
    } else if (group.name == "Glass Thickness") {
      //this.glassDepth = item.ref;
      this.sizeInfo.glassDepth = item.ref;
      this.setAttributes("glassDepth", item.ref);
    } else if (group.name == "Glass Color") {
      this.glassColor = item.name;
      this.setAttributes("glassColor", item);
      Balcony._applyParameters(glassMaterial, item.ref[0]);
      Balcony._applyParameters(glassSideMaterial, item.ref[1]);
      Balcony._applyParameters(glassInterMaterial, item.ref[2]);
    } else if (group.name == "Profil Color") {
      this.profileColor = item.name;
      this.sizeInfo.profileColor = item.name;
      this.setAttributes("profileColor", item);
      Balcony._applyParameters(baseMaterial, item.ref);
    }
    this.setLineDirty();
  }

  private setLineDirty() {
    this.isDirty = true;
    let n = nextWall(this);
    while (n && n instanceof Balcony && !n.isDirty) {
      n.isDirty = true;
      n.fixation = this.fixation;
      n.fixationName = this.fixationName;
      n.sizeInfo.glassDepth = this.sizeInfo.glassDepth;
      n.sizeInfo.glassHeight = this.sizeInfo.glassHeight;
      n.glassColor = this.glassColor;
      n.profileColor = this.profileColor;
      n.sizeInfo.glassLowerProfile = this.sizeInfo.glassLowerProfile;
      n.sizeInfo.glassHeight = this.sizeInfo.glassHeight;
      n = nextWall(n);
    }
    n = prevWall(this);
    while (n && n instanceof Balcony && !n.isDirty) {
      n.isDirty = true;
      n.fixation = this.fixation;
      n.fixationName = this.fixationName;
      n.sizeInfo.glassDepth = this.sizeInfo.glassDepth;
      n.sizeInfo.glassHeight = this.sizeInfo.glassHeight;
      n.glassColor = this.glassColor;
      n.profileColor = this.profileColor;
      n.sizeInfo.glassLowerProfile = this.sizeInfo.glassLowerProfile;
      n.sizeInfo.glassHeight = this.sizeInfo.glassHeight;
      n = prevWall(n);
    }
  }

  get sizeInfo() {
    return this._executor as any;
  }

  // sb24 top, sb360a side fixation, sb360 top
  //_getSizeInfo only works for sb24
  get isConstruct(): boolean {
    return true;
  }

  get isBalcony(): boolean {
    return true;
  }

  get visible() {
    return this._isVisible;
  }

  set visible(value) {
    this._isVisible = value;
  }

  private _visibility = false;
  set visibility(value: boolean) {
    let _isMeterVisible = false;
    //bad code
    if (Engine.activeEngine.activeCamera?.instance.type != "OrthographicCamera")
      _isMeterVisible = !value;
    else _isMeterVisible = value;

    if (this._visibility == value && this._isMeterVisible == _isMeterVisible)
      return;

    this._visibility = value;
    this._isMeterVisible = _isMeterVisible;
    this.isDirty = true;
  }

  get scaleObb(): Vector3 {
    this._scaleObb.set(this.length, 1.01 * 0.15, 0.02);
    return this._scaleObb;
  }

  private _clearRender() {
    this._root?.removeFromParent();
    this._root?.clear();
    //clear meter
    this._meter?.removeFromParent();
    this._meterVertical?.dispose();
    this._executor?.dispose();
  }

  private _renderVerticalMeter(scene?: Scene, camera?: CameraInterface) {
    let height = this._executor?.parameters.height;
    let heightOffset = this._executor?.parameters.heightOffset;

    let p = prev(this, (w) => w.type === "Balcony");
    let n = next(this, (w) => w.type === "Balcony");

    heightOffset = heightOffset ? heightOffset : 0.0;
    if (!height || (p && n)) return;
    let endPoint = new Point();
    endPoint.point = new Vector3(0, height, 0);
    let startPoint = new Point();
    startPoint.point = new Vector3(0, 0.0, 0);

    this._meterVertical?.dispose();
    this._meterVertical = new Meter(undefined, startPoint, endPoint, {
      minScale: 0.75,
      maxScale: 0.75,
      scale: new Vector3(0.75, 1, 1),
      debug: true,
      offset: new Vector3(0.0, 0.0, 0.0),
      rotation: new Euler(0, 0, -Math.PI / 2),
    });
    this._meterVertical.render(scene, camera);
    this._meterVertical.root.position.set(
      (n ? -1 : 1) * (this.length * 0.5 + 0.2),
      0.5 + heightOffset * 0.5,
      0.06
    );
    this._root?.add(this._meterVertical.root);
  }

  private _render3d(scene?: Scene, camera?: CameraInterface) {
    this._executor?.execute(this._root!);
    this._root?.position.copy(this._point!);
    this._root?.quaternion.copy(this.rotation!);
    scene?.add(this._root!);
  }

  render3d(scene?: Scene, camera?: CameraInterface): void {
    this.updateTransform();
    this.updateObb();
    this._clearRender();
    this._render3d(scene, camera);
    if (this._isMeterVisible) {
      this.renderMeter(scene, camera);
      this._renderVerticalMeter(scene, camera);
    }

    this.updateCameraReuest();
    /**/
  }

  public render2d(scene?: Scene, camera?: CameraInterface): void {
    this._executor?.dispose();
    super.render2d(scene, camera);
  }

  updateCameraReuest() {
    Engine.activeEngine.dispatchCustomEvent("camera-change", {
      detail: Engine.activeEngine.activeCamera?.instance,
    });
    Engine.activeEngine.dispatchCustomEvent("camera-end", {
      detail: Engine.activeEngine.activeCamera?.instance,
    });
    Engine.activeEngine.dispatchCustomEvent("request-update");
  }

  dispose(): void {
    super.dispose();
    this._clearRender();
  }

  removeFromParent(): void {
    super.removeFromParent();
    this._meterVertical?.root.removeFromParent();
  }

  export(accumulated: ExportAccumulator, walls: WallExportData[]): void {
    if (!this._executor?.isExecuted && this.root) {
      this._executor?.execute(this.root);
    }

    const exporter = new BalconyExporter(
      this._profileData,
      this._glassData,
      this.sizeInfo,
      this.profileColor,
      this.glassColor,
      this.name
    );

    exporter.export(accumulated, walls);

    logger.info("accumulated", accumulated);
  }
}
